import React, { useEffect, useState } from 'react';
import { Download, FileText } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Downloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [subName, setSubName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const [dlRes, pageRes] = await Promise.all([
        axios.get(`${API_URL}/downloads`),
        axios.get(`${API_URL}/page-content/downloads`)
      ]);
      setDownloads(dlRes.data);
      setPageContent(pageRes.data);
    } catch (error) {
      console.error('Error fetching downloads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (download) => {
    if (download.url && download.url !== '#') {
      // Open the file URL in a new tab
      window.open(download.url, '_blank');
      toast.success(`Downloading: ${download.title}`);
    } else {
      toast.info('Download file not yet uploaded. Contact admin.');
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setSubscribing(true);
    try {
      await axios.post(`${API_URL}/newsletter/subscribe`, { name: subName, email, source: 'downloads' });
      toast.success('Thank you for subscribing!');
      setEmail('');
      setSubName('');
    } catch (error) {
      console.error('Error subscribing:', error);
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-sage-50 via-lavender-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-serif text-gray-900">{pageContent?.title || 'Free Resources'}</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {pageContent?.description || 'Download helpful guides, checklists, and tools to support your healing journey'}
            </p>
          </div>
        </div>
      </section>

      {/* Downloads Section */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {downloads.map((download) => (
              <Card
                key={download.id}
                className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
              >
                <CardContent className="p-8 space-y-6">
                  <div className="flex items-start justify-between">
                    <div className="h-14 w-14 bg-sage-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <FileText className="h-7 w-7 text-sage-700" />
                    </div>
                    <div className="bg-lavender-100 px-3 py-1 rounded-full">
                      <span className="text-sm font-medium text-lavender-800">{download.fileType}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{download.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{download.description}</p>
                  </div>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <span className="text-sm text-gray-500">{download.size}</span>
                    <Button
                      onClick={() => handleDownload(download)}
                      className="bg-sage-700 hover:bg-sage-800 text-white rounded-lg transition-colors"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-sage-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl bg-white">
            <CardContent className="p-12 text-center space-y-6">
              <h2 className="text-3xl md:text-4xl font-serif text-gray-900">
                Get More Resources
              </h2>
              <p className="text-lg text-gray-600">
                Subscribe to receive exclusive resources, updates, and support straight to your inbox
              </p>
              <form onSubmit={handleSubscribe} className="flex flex-col gap-4 max-w-xl mx-auto">
                <input
                  type="text"
                  value={subName}
                  onChange={(e) => setSubName(e.target.value)}
                  placeholder="Your name"
                  data-testid="subscribe-name-input"
                  className="w-full px-6 py-4 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                />
                <div className="flex flex-col sm:flex-row gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="subscribe-email-input"
                    className="flex-1 px-6 py-4 rounded-xl border-2 border-sage-200 focus:border-sage-500 focus:outline-none transition-colors"
                  />
                  <Button
                    type="submit"
                    disabled={subscribing}
                    data-testid="subscribe-btn"
                    className="bg-sage-700 hover:bg-sage-800 text-white px-8 py-4 rounded-xl transition-all duration-300"
                  >
                    {subscribing ? 'Subscribing...' : 'Subscribe'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Downloads;